import { useStore } from 'hooks/useStore';
import {
  DefaultBpmByPillCount,
  DefaultBpmByPillCountPayload
} from 'Interfaces/ReferenceInterfaces/DefaultBpmByPillCountInterface';
import { useEffect, useRef, useState } from 'react';
import { DataTableStateEvent } from 'primereact/datatable';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';
import {toast} from 'react-toastify';

interface FormData {
  productPillCount: number;
  controllingMachineBpm: number;
}

export const useDefaultBpmByPillCountTable = (pageSize: number = 100) => {
  const { defaultBpmByPillCountStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<DefaultBpmByPillCount | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  // Load initial data when component mounts
  useEffect(() => {
    defaultBpmByPillCountStore.fetchDefaultBpmByPillCounts(currentPage, pageSize);
  }, [currentPage, pageSize]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await defaultBpmByPillCountStore.pollDefaultBpmByPillCounts(currentPage, pageSize);
    } catch (error) {
      if(environment === 'development'){
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for BPM by Pill Count');
      // Don't break the polling cycle on error
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1; // PrimeReact uses 0-based indexing, our API uses 1-based
      setCurrentPage(newPage);
      defaultBpmByPillCountStore.fetchDefaultBpmByPillCounts(newPage, pageSize);
    }
  };

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      // Edit existing row
      const updatePayload: DefaultBpmByPillCountPayload = {
        product_pill_count: formData.productPillCount,
        controlling_machine_bpm: formData.controllingMachineBpm
      };

      const success = await defaultBpmByPillCountStore.updateDefaultBpmByPillCount(
        selectedRow.id,
        updatePayload
      );
      if (!success) {
        throw new Error('Failed to update default BPM by pill count');
      }
    } else {
      // Add new row
      const createPayload: DefaultBpmByPillCountPayload = {
        product_pill_count: formData.productPillCount,
        controlling_machine_bpm: formData.controllingMachineBpm,
        flag: true
      };

      const success = await defaultBpmByPillCountStore.createDefaultBpmByPillCount(createPayload);
      if (!success) {
        throw new Error('Failed to create default BPM by pill count');
      }
    }
    setSelectedRow(null);
    // Refresh data while maintaining current page
    await defaultBpmByPillCountStore.fetchDefaultBpmByPillCounts(currentPage, pageSize);
  };

  // Update the handleDeleteRow function
  const handleDeleteRow = async () => {
    if (selectedRow) {
      await defaultBpmByPillCountStore.deleteDefaultBpmByPillCount(selectedRow.id);
      setSelectedRow(null);
      setShowDeleteModal(false);
      // Refresh data while maintaining current page
      await defaultBpmByPillCountStore.fetchDefaultBpmByPillCounts(currentPage, pageSize);
    }
  };

  const prepareFormData = (row: DefaultBpmByPillCount): FormData => {
    return {
      productPillCount: row.product_pill_count,
      controllingMachineBpm: row.controlling_machine_bpm
    };
  };

  // Method to manually refresh data
  const refreshData = async () => {
    await defaultBpmByPillCountStore.refreshData(currentPage, pageSize);
  };

  const handleExportToExcel = () => {
    const exportData = defaultBpmByPillCountStore.defaultBpmByPillCounts.map(item => ({
      'Pill Count': item.product_pill_count,
      'Controlling Machine BPM': item.controlling_machine_bpm
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Default BPM by Pill Count');
    XLSX.writeFile(workbook, 'default_bpm_by_pill_count_export.xlsx');
  };

  return {
    defaultBpmByPillCountData: defaultBpmByPillCountStore.defaultBpmByPillCounts,
    isLoading: defaultBpmByPillCountStore.isLoading,
    pagination: defaultBpmByPillCountStore.pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    prepareFormData,
    onPageChange,
    currentPage,
    refreshData,
    handleExportToExcel
  };
};
